// Turns the media markup the CMS (CKEditor 5) writes into body_xhtml into playable embeds:
//   <figure class="media"><oembed url="https://www.youtube.com/watch?v=ID"></oembed></figure>
//   <figure class="video" data-mode="gif|player"><video src="…"></video></figure>

export type MediaEmbedVariant = 'feed' | 'full';

const ENHANCED_ATTR = 'data-media-embed-enhanced';
const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const YOUTUBE_HOSTS = [
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
];
const YOUTUBE_PATH_PREFIXES = ['shorts', 'embed', 'live', 'v'];
const YOUTUBE_IFRAME_ALLOW =
  'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
const GIF_VIDEO_SELECTOR = 'figure.video[data-mode="gif"] video';

export function parseYouTubeId(url: string | null | undefined): string | null {
  const raw = url?.trim();

  if (!raw) return null;

  // CKEditor keeps the URL as typed, so it may come without a scheme.
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw.replace(/^\/\//, '')}`;

  let parsed: URL;

  try {
    parsed = new URL(withScheme);
  } catch {
    return null;
  }

  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;

  const host = parsed.hostname.toLowerCase();
  const segments = parsed.pathname.split('/').filter(Boolean);
  let id: string | null | undefined = null;

  if (host === 'youtu.be') {
    id = segments[0];
  } else if (YOUTUBE_HOSTS.includes(host)) {
    if (segments[0] === 'watch') {
      id = parsed.searchParams.get('v');
    } else if (YOUTUBE_PATH_PREFIXES.includes(segments[0])) {
      id = segments[1];
    }
  }

  return id && YOUTUBE_ID.test(id) ? id : null;
}

function createYouTubeIframe(id: string, autoplay: boolean): HTMLIFrameElement {
  const iframe = document.createElement('iframe');

  iframe.setAttribute('src', `https://www.youtube.com/embed/${id}${autoplay ? '?autoplay=1' : ''}`);
  iframe.setAttribute('title', 'YouTube video');
  iframe.setAttribute('allow', YOUTUBE_IFRAME_ALLOW);
  iframe.setAttribute('allowfullscreen', '');
  iframe.setAttribute('loading', 'lazy');
  iframe.setAttribute('frameborder', '0');
  // YouTube's own embed snippet sets this; the player refuses to load without a referrer.
  iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');

  return iframe;
}

// Feed items only show a thumbnail; the player iframe is loaded on demand.
function createYouTubePlaceholder(id: string): HTMLButtonElement {
  const button = document.createElement('button');

  button.type = 'button';
  button.className = 'media-embed-placeholder';
  button.setAttribute('data-youtube-id', id);
  button.setAttribute('aria-label', 'Play video');

  const thumbnail = document.createElement('img');

  thumbnail.setAttribute('src', `https://img.youtube.com/vi/${id}/hqdefault.jpg`);
  thumbnail.setAttribute('alt', '');
  thumbnail.setAttribute('loading', 'lazy');

  const playIcon = document.createElement('span');

  playIcon.className = 'media-embed-play-icon';
  playIcon.setAttribute('aria-hidden', 'true');

  button.appendChild(thumbnail);
  button.appendChild(playIcon);

  // Bound to the button itself so it lives exactly as long as the placeholder does, and
  // stops the click before the body's image-zoom handler or the feed item's own handlers.
  button.addEventListener('click', event => {
    event.preventDefault();
    event.stopPropagation();
    button.replaceWith(createYouTubeIframe(id, true));
  });

  return button;
}

function enhanceYouTubeEmbeds(root: HTMLElement, variant: MediaEmbedVariant) {
  root.querySelectorAll<HTMLElement>('figure.media').forEach(figure => {
    if (figure.hasAttribute(ENHANCED_ATTR)) return;

    const oembed = figure.querySelector('oembed[url]');
    const id = parseYouTubeId(oembed?.getAttribute('url'));

    // Non-YouTube oembeds are left exactly as authored.
    if (!oembed || !id) return;

    const wrapper = document.createElement('div');

    wrapper.className = 'media-embed-responsive';
    wrapper.appendChild(
      variant === 'full' ? createYouTubeIframe(id, false) : createYouTubePlaceholder(id),
    );

    oembed.replaceWith(wrapper);
    figure.setAttribute(ENHANCED_ATTR, '');
  });
}

function playGifVideo(video: HTMLVideoElement) {
  // Safari only allows unattended playback of muted videos.
  video.muted = true;

  const played = video.play() as Promise<void> | undefined;

  played?.catch(() => {});
}

// In feeds, GIF-like clips only download and play while at least half visible.
function enhanceGifVideos(root: HTMLElement): () => void {
  const videos = Array.from(root.querySelectorAll<HTMLVideoElement>(GIF_VIDEO_SELECTOR));

  if (videos.length === 0 || typeof IntersectionObserver === 'undefined') return () => {};

  const observer = new IntersectionObserver(
    entries => {
      for (const entry of entries) {
        const video = entry.target as HTMLVideoElement;

        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          playGifVideo(video);
        } else {
          video.pause();
        }
      }
    },
    { threshold: 0.5 },
  );

  for (const video of videos) {
    if (!video.hasAttribute(ENHANCED_ATTR)) {
      video.setAttribute(ENHANCED_ATTR, '');
      video.preload = 'none';
      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.muted = true;
      video.pause();
    }

    observer.observe(video);
  }

  return () => observer.disconnect();
}

export function enhanceMediaEmbeds(root: HTMLElement, variant: MediaEmbedVariant): () => void {
  enhanceYouTubeEmbeds(root, variant);

  // In full article views videos are left as authored.
  return variant === 'feed' ? enhanceGifVideos(root) : () => {};
}

// For hidden layout-measuring copies (see LineClamp): replaces every embed that would load
// media with an empty 16:9 box of a similar footprint, so measuring never fetches media.
// The <figure> itself (margins, caption) is kept so the measured height stays close.
export function stripMediaForMeasure(xhtml: string): string {
  if (!xhtml || !/<(video|oembed)\b/i.test(xhtml)) return xhtml;

  const doc = new DOMParser().parseFromString(xhtml, 'text/html');
  const media: Element[] = [
    ...Array.from(doc.body.querySelectorAll('figure.video video')),
    // Non-YouTube oembeds render nothing either way, so they keep their (empty) footprint.
    ...Array.from(doc.body.querySelectorAll('figure.media oembed[url]')).filter(
      oembed => !!parseYouTubeId(oembed.getAttribute('url')),
    ),
  ];

  for (const element of media) {
    const box = doc.createElement('div');

    box.className = 'media-embed-measure';
    element.replaceWith(box);
  }

  return media.length > 0 ? doc.body.innerHTML : xhtml;
}
